// ===============================================================
// ✅ CallChatSheet.js — Livora In-Call Glass Chat (CLEAN)
// ✅ reads from calls/{callId}/messages
// ✅ sends via sendCallMessage
// ✅ chat only
// ✅ gift button only triggers parent gift store
// ===============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

function toMs(v) {
  try {
    if (!v) return 0;
    if (typeof v?.toMillis === "function") return v.toMillis();
    if (typeof v === "number") return v;
    return 0;
  } catch {
    return 0;
  }
}

export default function CallChatSheet({
  open,
  callId,
  me,
  otherUser,
  onGiftClick,
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);

  const listRef = useRef(null);

  const functions = useMemo(() => getFunctions(undefined, "us-central1"), []);
  const sendCallMessageFn = useMemo(
    () => httpsCallable(functions, "sendCallMessage"),
    [functions]
  );

  const myUid = me?.id || auth.currentUser?.uid || "";
  const otherUid = otherUser?.id || "";

  useEffect(() => {
    if (!open || !callId) return;

    const q = query(
      collection(db, "calls", callId, "messages"),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];

        snap.forEach((d) => {
          list.push({ id: d.id, ...d.data() });
        });

        list.sort((a, b) => {
          const aMs = a.createdAtMs || toMs(a.createdAt);
          const bMs = b.createdAtMs || toMs(b.createdAt);
          return aMs - bMs;
        });

        setMessages(list);
      },
      (err) => {
        console.error("CallChatSheet snapshot error:", err);
      }
    );

    return () => unsub();
  }, [open, callId]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages, open]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!open || !text || !callId || !myUid || !otherUid || sending) return;

    try {
      setSending(true);

      await sendCallMessageFn({
        callId,
        text,
      });

      setInputText("");
    } catch (e) {
      console.error("CallChatSheet send error:", e);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="callchat-root">
      <div ref={listRef} className="callchat-messages">
        {messages.map((m) => {
          const mine = m?.fromUid === myUid;

          const text =
            typeof m?.text === "string"
              ? m.text
              : typeof m?.message === "string"
              ? m.message
              : "";

          return (
            <div
              key={m.id}
              className={`callchat-line ${mine ? "mine" : "theirs"}`}
            >
              <span className="callchat-text">{text}</span>
            </div>
          );
        })}
      </div>

      <div className="callchat-inputbar">
        <textarea
          className="callchat-input"
          rows={1}
          dir="auto"
          value={inputText}
          placeholder={t("calls.chat.placeholder", { defaultValue: "Write a message..." })}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />

        <button
          type="button"
          className="callchat-gift-btn"
          onClick={() => onGiftClick?.()}
          title={t("calls.chat.gifts_title", { defaultValue: "Gifts" })}
        >
          🎁
        </button>

        <button
          type="button"
          className="callchat-send-btn"
          onClick={handleSend}
          disabled={sending}
          title={t("calls.chat.send_title", { defaultValue: "Send" })}
        >
          {sending ? "..." : "➤"}
        </button>
      </div>
    </div>
  );
}
